import React from "react";
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  spring,
  interpolate,
} from "remotion";
import { COLORS, FONTS, SPRING } from "./constants";
import { RainbowEAKC } from "../shared/RainbowEAKC";

const ORBS = [
  { color: COLORS.neonPink,   angle: 0   },
  { color: COLORS.neonBlue,   angle: 60  },
  { color: COLORS.neonPurple, angle: 120 },
  { color: COLORS.neonGreen,  angle: 180 },
  { color: COLORS.neonOrange, angle: 240 },
  { color: COLORS.neonCyan,   angle: 300 },
];

const PARTICLES = [
  { x: 120,  y: 80,  size: 6,  color: COLORS.neonPink,   freq: 0.04, phase: 0   },
  { x: 1750, y: 150, size: 8,  color: COLORS.neonBlue,   freq: 0.03, phase: 1   },
  { x: 200,  y: 900, size: 5,  color: COLORS.neonGreen,  freq: 0.05, phase: 2   },
  { x: 1800, y: 850, size: 7,  color: COLORS.neonOrange, freq: 0.04, phase: 0.5 },
  { x: 960,  y: 60,  size: 6,  color: COLORS.neonCyan,   freq: 0.06, phase: 1.5 },
  { x: 960,  y: 980, size: 5,  color: COLORS.neonYellow, freq: 0.03, phase: 2.5 },
  { x: 350,  y: 300, size: 4,  color: COLORS.neonPurple, freq: 0.07, phase: 0.3 },
  { x: 1600, y: 400, size: 6,  color: COLORS.neonPink,   freq: 0.05, phase: 1.2 },
  { x: 100,  y: 500, size: 5,  color: COLORS.neonGreen,  freq: 0.04, phase: 2.1 },
  { x: 1850, y: 550, size: 7,  color: COLORS.neonBlue,   freq: 0.06, phase: 0.8 },
  { x: 500,  y: 950, size: 4,  color: COLORS.neonCyan,   freq: 0.05, phase: 1.7 },
  { x: 1400, y: 100, size: 6,  color: COLORS.neonOrange, freq: 0.04, phase: 3.0 },
];

export const Scene01Title: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps, durationInFrames } = useVideoConfig();

  const ringScale = spring({ frame, fps, config: SPRING.slow });
  const ringRotation = frame * 0.8;

  const titleAppear = spring({ frame: frame - 10, fps, config: SPRING.bouncy });
  const subtitleAppear = spring({ frame: frame - 30, fps, config: SPRING.gentle });
  const taglineAppear = spring({ frame: frame - 60, fps, config: SPRING.gentle });

  const exitOpacity = interpolate(frame, [durationInFrames - 30, durationInFrames], [1, 0], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });

  const gridPulse = interpolate(
    Math.sin(frame * 0.05),
    [-1, 1],
    [0.04, 0.10],
  );

  const containerTilt: React.CSSProperties = {
    perspective: "1200px",
    width: "100%",
    height: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  };

  const innerTilt: React.CSSProperties = {
    transform: `rotateX(5deg) translateZ(${Math.sin(frame * 0.01) * 3}px)`,
    width: "100%",
    height: "100%",
    position: "relative",
  };

  return (
    <AbsoluteFill
      style={{
        background: COLORS.bg,
        opacity: exitOpacity,
        overflow: "hidden",
      }}
    >
      {/* Animated SVG grid background */}
      <AbsoluteFill>
        <svg width="1920" height="1080" style={{ opacity: gridPulse }}>
          <defs>
            <pattern id="grid" width="60" height="60" patternUnits="userSpaceOnUse">
              <path
                d="M 60 0 L 0 0 0 60"
                fill="none"
                stroke={COLORS.neonBlue}
                strokeWidth="0.5"
              />
            </pattern>
          </defs>
          <rect width="1920" height="1080" fill="url(#grid)" />
        </svg>
      </AbsoluteFill>

      {/* Parallax depth layers */}
      <div style={{
        position: "absolute",
        inset: 0,
        transform: `translateY(${Math.sin(frame * 0.008) * 8}px) translateX(${Math.cos(frame * 0.006) * 5}px)`,
        pointerEvents: "none",
      }}>
        {/* Slow-moving background orbs */}
        {[
          { x: 150, y: 200, size: 400, color: COLORS.neonPink, speed: 0.005, phase: 0 },
          { x: 1600, y: 300, size: 350, color: COLORS.neonBlue, speed: 0.004, phase: 2 },
          { x: 900, y: 800, size: 500, color: COLORS.neonPurple, speed: 0.003, phase: 4 },
        ].map((orb, i) => (
          <div key={`parallax-${i}`} style={{
            position: "absolute",
            left: orb.x + Math.sin(frame * orb.speed + orb.phase) * 30 - orb.size / 2,
            top: orb.y + Math.cos(frame * orb.speed * 0.7 + orb.phase) * 20 - orb.size / 2,
            width: orb.size,
            height: orb.size,
            borderRadius: "50%",
            background: `radial-gradient(circle, ${orb.color}06 0%, transparent 70%)`,
          }} />
        ))}
      </div>

      {/* Mid-layer floating particles */}
      {[...Array(6)].map((_, i) => {
        const px = 200 + i * 300;
        const py = 150 + (i % 3) * 300;
        const pColor = [COLORS.neonPink, COLORS.neonBlue, COLORS.neonGreen, COLORS.neonOrange, COLORS.neonPurple, COLORS.neonCyan][i];
        return (
          <div key={`particle-mid-${i}`} style={{
            position: "absolute",
            left: px + Math.sin(frame * 0.02 + i * 1.5) * 15,
            top: py + Math.cos(frame * 0.015 + i * 2) * 12,
            width: 4 + (i % 3) * 2,
            height: 4 + (i % 3) * 2,
            borderRadius: "50%",
            background: pColor,
            boxShadow: `0 0 8px ${pColor}80, 0 0 16px ${pColor}40`,
            opacity: 0.5 + Math.sin(frame * 0.03 + i) * 0.2,
            pointerEvents: "none",
          }} />
        );
      })}

      {/* Floating particles */}
      <AbsoluteFill style={{ pointerEvents: "none" }}>
        {PARTICLES.map((p, i) => {
          const floatY = Math.sin(frame * p.freq + p.phase) * 18;
          const floatX = Math.cos(frame * p.freq * 0.7 + p.phase) * 10;
          const particleAppear = interpolate(frame, [i * 3, i * 3 + 20], [0, 1], {
            extrapolateLeft: "clamp",
            extrapolateRight: "clamp",
          });
          return (
            <div
              key={i}
              style={{
                position: "absolute",
                left: p.x,
                top: p.y + floatY,
                transform: `translateX(${floatX}px)`,
                width: p.size,
                height: p.size,
                borderRadius: "50%",
                background: p.color,
                opacity: particleAppear * 0.85,
                boxShadow: `0 0 ${p.size * 4}px ${p.color}, 0 0 ${p.size * 8}px ${p.color}60`,
                filter: `blur(${p.size > 6 ? 1 : 0}px)`,
              }}
            />
          );
        })}
      </AbsoluteFill>

      {/* 3D rotating orb ring */}
      <AbsoluteFill
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          pointerEvents: "none",
        }}
      >
        <div
          style={{
            position: "relative",
            width: 500,
            height: 500,
            transform: `scale(${ringScale})`,
          }}
        >
          {ORBS.map((orb, i) => {
            const angleRad = ((orb.angle + ringRotation) * Math.PI) / 180;
            const radius = 220;
            const x = Math.cos(angleRad) * radius;
            const y = Math.sin(angleRad) * radius * 0.45; // flatten for 3D effect
            const zDepth = Math.sin(angleRad);
            const scale = interpolate(zDepth, [-1, 1], [0.6, 1.2]);
            const opacity = interpolate(zDepth, [-1, 1], [0.4, 1]);
            const size = 20 + scale * 8;

            return (
              <div
                key={i}
                style={{
                  position: "absolute",
                  left: "50%",
                  top: "50%",
                  width: size,
                  height: size,
                  borderRadius: "50%",
                  background: orb.color,
                  transform: `translate(calc(-50% + ${x}px), calc(-50% + ${y}px))`,
                  opacity,
                  boxShadow: `0 0 20px ${orb.color}, 0 0 40px ${orb.color}80, 0 0 80px ${orb.color}40`,
                  zIndex: Math.round(zDepth * 10 + 10),
                }}
              />
            );
          })}
          {/* Ring ellipse */}
          <svg
            style={{ position: "absolute", top: 0, left: 0 }}
            width={500}
            height={500}
            viewBox="-250 -250 500 500"
          >
            <ellipse
              rx={220}
              ry={99}
              fill="none"
              stroke={`${COLORS.neonBlue}40`}
              strokeWidth={1}
              strokeDasharray="8 6"
            />
          </svg>
        </div>
      </AbsoluteFill>

      {/* 3D perspective container for main content */}
      <AbsoluteFill style={containerTilt}>
        <div style={innerTilt}>
          {/* Main title EAKC */}
          <div
            style={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: `translate(-50%, -65%)`,
              textAlign: "center",
            }}
          >
            <RainbowEAKC
              id="vr-title"
              frame={frame}
              fontSize={120}
              fontFamily={FONTS.heading}
              scale={interpolate(titleAppear, [0, 1], [0.7, 1])}
              opacity={interpolate(titleAppear, [0, 1], [0, 1])}
              glowColor={COLORS.neonPink}
            />
          </div>

          {/* Subtitle */}
          <div
            style={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: `translate(-50%, 10px) translateY(${interpolate(subtitleAppear, [0, 1], [20, 0])}px)`,
              opacity: interpolate(subtitleAppear, [0, 1], [0, 1]),
              textAlign: "center",
              whiteSpace: "nowrap",
            }}
          >
            <span
              style={{
                fontSize: 36,
                fontFamily: FONTS.body,
                fontWeight: 300,
                color: COLORS.textPrimary,
                letterSpacing: 6,
                textTransform: "uppercase",
              }}
            >
              Platform Vision &amp; Roadmap
            </span>
          </div>

          {/* Tagline */}
          <div
            style={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: `translate(-50%, 80px) translateY(${interpolate(taglineAppear, [0, 1], [20, 0])}px)`,
              opacity: interpolate(taglineAppear, [0, 1], [0, 1]),
              textAlign: "center",
              whiteSpace: "nowrap",
            }}
          >
            <span
              style={{
                fontSize: 22,
                fontFamily: FONTS.mono,
                fontWeight: 500,
                color: COLORS.neonCyan,
                textShadow: `0 0 20px ${COLORS.neonCyan}80, 0 0 40px ${COLORS.neonCyan}40`,
                letterSpacing: 2,
              }}
            >
              From Knowledge Base → Autonomous Platform
            </span>
          </div>
        </div>
      </AbsoluteFill>
    </AbsoluteFill>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
